/**
 * KeyValueRow — label / value row.
 * KeyValueList — vertical group of rows with consistent spacing and dividers.
 *
 * Default layout is **inline**: renders `Label: value` on a single flowing
 * line so the value wraps naturally as the container narrows. Long IDs,
 * addresses, ISO timestamps, badges and pills all sit nicely without the
 * left/right tug-of-war that the legacy split caused on resize.
 *
 * The split layout (label left, value right) is still available via
 * `layout="split"` for tabular dashboards where a column of right-aligned
 * single-word values reads better. The split variant is also responsive:
 * `KeyValueList` declares a CSS container, and rows automatically collapse
 * to a stacked layout when the container falls below ~260 px.
 *
 * @example
 *   // Default inline — "EVSE ID: DE*WLE*ETEST003" flows on one line.
 *   <KeyValueList>
 *     <KeyValueRow label="EVSE ID" value={<MonoText>DE*WLE*ETEST003</MonoText>} />
 *     <KeyValueRow label="Power"   value="11 kW" />
 *     <KeyValueRow label="Status"  value={<StatusBadge status="success" label="online" />} />
 *   </KeyValueList>
 *
 * @example
 *   // Split — label left, value right. Auto-stacks on narrow containers.
 *   <KeyValueList layout="split">
 *     <KeyValueRow layout="split" label="Connectors" value={4} />
 *     <KeyValueRow layout="split" label="SLA tier"   value={<Pill color="informative">standard</Pill>} />
 *   </KeyValueList>
 *
 * Migration note: the old `inline` boolean prop is still accepted —
 * `inline` (default true) and `layout="split"` produce the same DOM as
 * the previous `inline` / split modes respectively. New code should use
 * `layout`; old `inline` callsites continue to work unchanged.
 */
import type { ReactNode } from 'react';
import { Body1, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { EMPTY_VALUE } from './emptyValue';

export type KeyValueLayout = 'inline' | 'split';

const useRowStyles = makeStyles({
  /* inline layout — `Label: value` on one flowing line. Wraps naturally
     when narrow because no flex constraints fight the text reflow. */
  inline: {
    display: 'block',
    minHeight: '24px',
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
    overflowWrap: 'anywhere',
  },
  inlineLabel: {
    color: tokens.colorNeutralForeground3,
    marginRight: tokens.spacingHorizontalXS,
  },
  /* split layout — label left, value right. Container query at the
     `KeyValueList` level (see useListStyles) flips this to a stacked
     two-row layout below ~260 px so labels never collide with values. */
  split: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    minHeight: '24px',
  },
  splitLabel: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  splitValue: {
    textAlign: 'right',
    minWidth: 0,
    overflowWrap: 'anywhere',
    color: tokens.colorNeutralForeground1,
  },
});

interface KeyValueRowProps {
  label: ReactNode;
  /** When omitted or null/undefined, an em-dash is rendered to keep alignment. */
  value?: ReactNode;
  /**
   * Visual layout. ``"inline"`` (default) reads `Label: value` on one
   * flowing line. ``"split"`` keeps label left + value right and is
   * useful for tabular dashboards.
   */
  layout?: KeyValueLayout;
  /**
   * @deprecated Use `layout="split"` to opt into the legacy split layout.
   * Retained for backward compatibility — when set, overrides ``layout``.
   * `inline={true}` → ``layout="inline"``, `inline={false}` → ``layout="split"``.
   */
  inline?: boolean;
}

function resolveLayout(
  layout: KeyValueLayout | undefined,
  inline: boolean | undefined,
): KeyValueLayout {
  if (inline !== undefined) return inline ? 'inline' : 'split';
  return layout ?? 'inline';
}

export function KeyValueRow({ label, value, layout, inline }: KeyValueRowProps) {
  const styles = useRowStyles();
  const hasValue = value !== undefined && value !== null && value !== '';
  const resolved = resolveLayout(layout, inline);
  if (resolved === 'split') {
    return (
      <div data-kv-layout="split" className={styles.split}>
        <Caption1 className={styles.splitLabel}>{label}</Caption1>
        <Body1 className={styles.splitValue}>{hasValue ? value : EMPTY_VALUE}</Body1>
      </div>
    );
  }
  return (
    <div data-kv-layout="inline" className={styles.inline}>
      <Caption1 as="span" className={styles.inlineLabel}>{label}:</Caption1>
      <Body1 as="span">{hasValue ? value : EMPTY_VALUE}</Body1>
    </div>
  );
}

const useListStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    /* Establish a CSS container so children using `layout="split"` can
       container-query the available inline size and switch to a stacked
       layout when the rail / drawer / column is too narrow. Costs zero
       runtime measuring and re-flows automatically on resize. */
    containerType: 'inline-size',
  },
  divided: {
    '> div + div': {
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      paddingTop: tokens.spacingVerticalXS,
      marginTop: tokens.spacingVerticalXS,
    },
  },
  spaced: {
    gap: tokens.spacingVerticalXS,
  },
  /* Container query — when the list is narrower than ~260 px, force any
     split-layout child to stack: label on top, value below, both
     left-aligned. Inline-layout children already wrap naturally and are
     unaffected. */
  responsive: {
    '@container (max-width: 260px)': {
      '& [data-kv-layout="split"]': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: tokens.spacingVerticalXXS,
      },
      '& [data-kv-layout="split"] > :nth-child(2)': {
        textAlign: 'left',
        width: '100%',
      },
    },
  },
});

interface KeyValueListProps {
  children: ReactNode;
  /** When true, draws a hairline divider between rows. */
  divided?: boolean;
}

export function KeyValueList({ children, divided }: KeyValueListProps) {
  const styles = useListStyles();
  return (
    <div
      className={mergeClasses(
        styles.root,
        styles.responsive,
        divided ? styles.divided : styles.spaced,
      )}
    >
      {children}
    </div>
  );
}
